import React, { Component, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { Table } from "antd";
import { getLawFirmsWithAttrsByArtUnit } from "../../services/api-prosecution";
import { FlexibleWidthXYPlot, XAxis, YAxis, MarkSeries, Hint } from "@unifiedpatents/euclid-react";
import { getAttributeScale } from "react-vis/dist/utils/scales-utils";
import { sorterNumberGen } from "../../helpers/table";
import { sumBy } from "lodash";

const SeparationLines = props => {
  const { _allData, innerHeight: height, innerWidth: width, marginTop, marginLeft, xDomain, yDomain } = props;
  const data = _allData[0];
  const scaler = {
    x: getAttributeScale(props, "x"),
    y: getAttributeScale(props, "y")
  };
  const avg = {
    x: scaler.x(sumBy(data, "x") / data.length),
    y: scaler.y(sumBy(data, "y") / data.length)
  };
  return avg.x > 0 && avg.y > 0 ? (
    <g transform={`translate(${marginLeft},${marginTop})`}>
      <line stroke="grey" strokeOpacity={0.5} x1={0} x2={width} y1={avg.y} y2={avg.y} />
      <line stroke="grey" strokeOpacity={0.5} x1={avg.x} x2={avg.x} y1={0} y2={height} />
      <rect fill="green" fillOpacity={0.1} x={avg.x} width={scaler.x(xDomain[1]) - avg.x} y={0} height={avg.y} />
      <rect fill="red" fillOpacity={0.1} x={0} width={avg.x} y={avg.y} height={scaler.y(yDomain[0]) - avg.y} />
    </g>
  ) : null;
};
SeparationLines.propTypes = {
  xDomain: PropTypes.array,
  yDomain: PropTypes.array,
  _allData: PropTypes.array,
  innerHeight: PropTypes.number,
  innerWidth: PropTypes.number,
  marginTop: PropTypes.number,
  marginLeft: PropTypes.number
};
SeparationLines.displayName = "SeparationLines";
SeparationLines.requiresSVG = true;

const ArtUnitLawFirmsChart = ({ data }) => {
  const [hintValue, setHintValue] = useState(data[0]); // highlight top no.1 by default
  const xDomain = data.reduce((res, row) => ({ max: Math.max(res.max, row.x), min: Math.min(res.min, row.x) }), { min: Infinity, max: -Infinity });
  const yDomain = data.reduce((res, row) => ({ max: Math.max(res.max, row.y), min: Math.min(res.min, row.y) }), { min: Infinity, max: -Infinity });
  return (
    <FlexibleWidthXYPlot height={300} xDomain={[xDomain.max, xDomain.min]} yDomain={[yDomain.max, yDomain.min]}>
      <XAxis title="Pendency (Years)" />
      <YAxis title="Broader" />
      <SeparationLines />
      <MarkSeries data={data} onValueMouseOver={setHintValue} onValueMouseOut={() => setHintValue(null)} sizeRange={[5, 15]} color="rgb(24, 144, 255)" />
      {hintValue ? (
        <Hint value={hintValue}>
          <div className="rv-hint__content">
            <p>
              <b>{hintValue.label}</b>
            </p>
            <dl>
              <dd>#Apps: {hintValue.size}</dd>
              <dd>Pendency: {hintValue.x.toFixed(2)}</dd>
              <dd>Narrowness: {hintValue.y.toFixed(2)}</dd>
            </dl>
          </div>
        </Hint>
      ) : null}
    </FlexibleWidthXYPlot>
  );
};
ArtUnitLawFirmsChart.propTypes = {
  data: PropTypes.array.isRequired
};

class ArtUnitLawFirms extends Component {
  static propTypes = {
    artUnit: PropTypes.string.isRequired
  };

  state = {
    data: []
  };

  componentDidMount() {
    getLawFirmsWithAttrsByArtUnit(this.props.artUnit)
      .then(data => this.setState({ data }))
      .catch(() => this.setState({ data: [] }));
  }

  render() {
    const { data } = this.state;
    return data && data.length ? (
      <div>
        <ArtUnitLawFirmsChart
          data={data.map(d => ({
            label: d.lf,
            x: d.p,
            y: d.n,
            size: d.t
          }))}
        />
        <Table
          size="small"
          rowKey="lf"
          dataSource={data}
          columns={[
            {
              dataIndex: "lf",
              title: "Law Firm",
              render: val => <Link to={`/prosecution/lawfirm/${val}`}>{val}</Link>
            },
            {
              dataIndex: "s",
              title: "LPIX",
              render: val => (val * 100).toFixed(1) + "%",
              sorter: sorterNumberGen("s")
            },
            {
              dataIndex: "ar",
              title: "Allowance Rate",
              render: val => (val * 100).toFixed(1) + "%",
              sorter: sorterNumberGen("ar")
            },
            {
              dataIndex: "t",
              title: "#Apps",
              sorter: sorterNumberGen("t")
            },
            {
              dataIndex: "p",
              title: "Pendency",
              sorter: sorterNumberGen("p")
            },
            {
              dataIndex: "n",
              title: "Narrowness",
              sorter: sorterNumberGen("n")
            }
          ]}
        />
      </div>
    ) : (
      <p>No data available.</p>
    );
  }
}

export default ArtUnitLawFirms;
